import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import "./App.css";
import {
  primeAudio,
  setMuted,
  isMuted,
  beep,
  keyClick,
  keyClack,
  startHDD,
  stopHDD,
  postBeep,
  errorTone,
  selectBeep,
  launchTone,
} from "./lib/audio";
import {
  BIOS_LINES,
  DOS_BOOT_LINES,
  CHKDSK_LINES,
  DOS_HEADER,
  HELP_TEXT,
  DIR_TEXT,
  TREE_TEXT,
  README_TEXT,
  MENU,
  VIEW_NAMES,
} from "./lib/dosContent";

// ---------- STATE MACHINE ----------
// off -> powering -> bios -> dosboot -> dos -> loading -> app -> (bsod optional) -> off

function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

function formatMT(d) {
  try {
    return (
      new Intl.DateTimeFormat("en-GB", {
        timeZone: "America/Denver",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).format(d) + " MT"
    );
  } catch (_) {
    return d.toTimeString().slice(0, 8) + " MT";
  }
}

// ================================================================
// POWER ON overlay - authentic CRT power up moment
// ================================================================
function PowerOnScreen({ onPowerOn }) {
  return (
    <motion.div
      className="power-on"
      data-testid="power-on-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="power-on-inner">
        <motion.div
          className="power-brand"
          initial={{ letterSpacing: "0.4em", opacity: 0 }}
          animate={{ letterSpacing: "0.6em", opacity: 1 }}
          transition={{ delay: 0.2, duration: 1.2, ease: "easeOut" }}
        >
          HEISENBERG&nbsp;DYNAMICS
        </motion.div>
        <motion.div
          className="power-tag"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
        >
          GENERATIVE SYSTEMS TERMINAL &mdash; W6168MS / VGA
        </motion.div>

        <motion.button
          type="button"
          className="power-btn"
          data-testid="power-on-button"
          onClick={onPowerOn}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.5 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <span className="power-btn-dot" /> PRESS TO POWER ON
        </motion.button>

        <motion.div
          className="power-hint"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.55 }}
          transition={{ delay: 1.6, duration: 0.6 }}
        >
          audio enabled on click &middot; press M to mute later
        </motion.div>
      </div>
    </motion.div>
  );
}

// ================================================================
// BIOS - POST screen with animating memory count + staggered lines
// ================================================================
function BiosScreen({ onSkip }) {
  const [visible, setVisible] = useState(0);
  const [memory, setMemory] = useState(0);
  const [bottomShown, setBottomShown] = useState(false);
  const totalLines = BIOS_LINES.length;

  useEffect(() => {
    postBeep();
    const timers = [];
    BIOS_LINES.forEach((_, index) => {
      timers.push(
        setTimeout(() => {
          setVisible((v) => Math.max(v, index + 1));
        }, 80 + index * 90)
      );
    });
    let mem = 0;
    const memI = setInterval(() => {
      mem = Math.min(65536, mem + 4096);
      setMemory(mem);
      if (mem >= 65536) clearInterval(memI);
    }, 32);
    const bottomT = setTimeout(() => setBottomShown(true), 1750);
    const autoAdvanceT = setTimeout(() => onSkip(), 2600);
    return () => {
      timers.forEach(clearTimeout);
      clearInterval(memI);
      clearTimeout(bottomT);
      clearTimeout(autoAdvanceT);
    };
  }, [onSkip]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "F1" || e.key === "Escape" || e.key === "Enter") {
        e.preventDefault();
        onSkip();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onSkip]);

  const memoryStr = String(memory).padStart(5, "0") + "K";

  return (
    <section className="stage bios active" data-testid="bios-screen">
      <div className="energy-logo" aria-label="Energy Star logo" />
      {BIOS_LINES.map((line, i) => {
        const show = i < visible || bottomShown;
        const cls = "bios-line" + (line.hot ? " bios-hot" : "") + (show ? " show" : "");
        return (
          <div key={i} className={cls}>
            {line.hasMemory ? (
              <>
                {line.text}
                {" "}
                <span className="mem-count">{memoryStr}</span>
                {" OK"}
              </>
            ) : (
              <>
                {line.text}
                {line.suffix && (
                  <span className={line.suffixClass === "ok" ? "bios-ok" : ""}>
                    {line.suffix}
                  </span>
                )}
              </>
            )}
          </div>
        );
      })}
      <div className={"bios-bottom" + (bottomShown ? " show" : "")}>
        Press <span className="bios-hot">F1</span> to continue,{" "}
        <span className="bios-hot">DEL</span> to enter SETUP
        <br />
        11/17/2000-i440ZX-W977-2A69KM4NC-00
      </div>
      <button
        type="button"
        className="skip-btn"
        data-testid="skip-bios-btn"
        onClick={onSkip}
      >
        [F1] CONTINUE &raquo;
      </button>
    </section>
  );
}

// ================================================================
// DOS BOOT sequence - MS-DOS load streams line-by-line + CHKDSK
// ================================================================
function DosBootScreen({ onComplete }) {
  const [lines, setLines] = useState([]);
  const [chkdskLine, setChkdskLine] = useState(-1);
  const [phase, setPhase] = useState("boot"); // boot -> chkdsk -> done

  useEffect(() => {
    startHDD(0.35);
    let cancelled = false;
    let acc = 0;
    const timers = [];
    DOS_BOOT_LINES.forEach((l) => {
      acc += l.delay;
      timers.push(
        setTimeout(() => {
          if (cancelled) return;
          setLines((prev) => [...prev, l.text]);
          if (l.isChkdsk) setPhase("chkdsk");
        }, acc)
      );
    });
    const chkStart = acc + 180;
    CHKDSK_LINES.forEach((_, i) => {
      timers.push(
        setTimeout(() => {
          if (cancelled) return;
          setChkdskLine(i);
        }, chkStart + i * 55)
      );
    });
    timers.push(
      setTimeout(() => {
        if (cancelled) return;
        setPhase("done");
        stopHDD();
        onComplete();
      }, chkStart + CHKDSK_LINES.length * 55 + 350)
    );
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      stopHDD();
    };
  }, [onComplete]);

  return (
    <section className="stage dosboot active" data-testid="dos-boot-screen">
      <pre className="dosboot-out">
        {lines.map((t, i) => t + "\n").join("")}
      </pre>
      {phase !== "boot" && (
        <pre className="dosboot-chk">
          {CHKDSK_LINES.slice(0, chkdskLine + 1).map((t) => t + "\n").join("")}
        </pre>
      )}
      <div className="dosboot-cursor">_</div>
    </section>
  );
}

// ================================================================
// DOS PROMPT - interactive shell with rich commands
// ================================================================
function DosPrompt({ onLaunch, onPowerOff }) {
  const [output, setOutput] = useState(DOS_HEADER);
  const [value, setValue] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [wrongCount, setWrongCount] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, []);

  const append = useCallback((text) => {
    setOutput((prev) => prev + "\n" + text);
  }, []);

  const runCommand = useCallback(
    (raw) => {
      const cmd = raw.trim();
      const lower = cmd.toLowerCase();
      append("C:\\>" + cmd);

      if (!cmd) return;
      if (lower === "heisenberg") {
        launchTone();
        append("Loading HEISENBERGDX.COM ...");
        setDisabled(true);
        startHDD(0.45);
        setTimeout(() => {
          stopHDD();
          onLaunch();
        }, 900);
        return;
      }
      if (lower === "help" || lower === "?") {
        HELP_TEXT.forEach((l) => append(l));
        return;
      }
      if (lower === "dir") {
        DIR_TEXT.forEach((l) => append(l));
        return;
      }
      if (lower === "tree") {
        TREE_TEXT.forEach((l) => append(l));
        return;
      }
      if (lower === "ver") {
        append("Heisenberg DOS Version 6.22  (build 4.86.2026)");
        return;
      }
      if (lower === "mem") {
        append("Memory Type       Total       Used       Free");
        append("--------------  --------   --------   --------");
        append("Conventional      640K       118K       522K");
        append("Upper             155K       118K        37K");
        append("Reserved          384K       384K         0K");
        append("Extended (XMS) 65,536K     8,192K    57,344K");
        append("--------------  --------   --------   --------");
        append("Total memory   66,715K     8,812K    57,903K");
        return;
      }
      if (lower === "time") {
        append("Current time is " + new Date().toTimeString().slice(0, 8) + ".00");
        return;
      }
      if (lower === "date") {
        append("Current date is " + new Date().toDateString());
        return;
      }
      if (lower === "whoami") {
        append("HEISENBERG\\OPERATOR");
        return;
      }
      if (lower === "cls" || lower === "clear") {
        setOutput("");
        return;
      }
      if (lower.startsWith("echo ")) {
        append(cmd.slice(5));
        return;
      }
      if (lower === "echo") {
        append("ECHO is on.");
        return;
      }
      if (lower === "type readme.txt" || lower === "type readme") {
        README_TEXT.forEach((l) => append(l));
        return;
      }
      if (lower === "exit") {
        append("Powering down...");
        setDisabled(true);
        setTimeout(() => onPowerOff(), 500);
        return;
      }
      if (lower === "format" || lower.startsWith("format ") || lower === "crash" || lower === "die") {
        append("Fatal exception 0E has occurred at 0028:C0011E36...");
        setDisabled(true);
        setTimeout(() => window.dispatchEvent(new CustomEvent("hdx-bsod")), 300);
        return;
      }

      const next = wrongCount + 1;
      setWrongCount(next);
      if (next >= 5) {
        append("System instability detected. Fatal exception 0E...");
        setDisabled(true);
        setTimeout(() => window.dispatchEvent(new CustomEvent("hdx-bsod")), 300);
        return;
      }
      append("Bad command or file name. Type HELP or HEISENBERG.");
      errorTone();
    },
    [append, onLaunch, onPowerOff, wrongCount]
  );

  const onSubmit = (e) => {
    e.preventDefault();
    keyClack();
    const val = value;
    setValue("");
    runCommand(val);
  };

  const onKeyDown = (e) => {
    if (e.key.length === 1) keyClick();
    if (e.key === "Backspace" || e.key === "Tab") keyClick();
  };

  return (
    <section
      className="stage dos active"
      data-testid="dos-prompt-screen"
      onClick={() => inputRef.current?.focus()}
    >
      <pre className="dos-output">{output}</pre>
      <div className="dos-instruction">
        Type <span className="bios-hot">HELP</span> for commands, or{" "}
        <span className="bios-hot">HEISENBERG</span> to load the website.
      </div>
      <form className="prompt-row" onSubmit={onSubmit} autoComplete="off">
        <label className="prompt-label" htmlFor="dos-cmd">C:\&gt;</label>
        <input
          ref={inputRef}
          id="dos-cmd"
          data-testid="dos-command-input"
          className="command-input"
          type="text"
          value={value}
          disabled={disabled}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          autoCapitalize="none"
          autoComplete="off"
          spellCheck={false}
        />
        <span className="dos-caret" aria-hidden="true">█</span>
      </form>
      <div className="dos-hint">
        AVAILABLE: HEISENBERG &middot; HELP &middot; DIR &middot; TREE &middot; VER &middot; MEM &middot; TIME &middot; DATE &middot; TYPE README.TXT &middot; CLS &middot; EXIT
      </div>
    </section>
  );
}

// ================================================================
// APP - Blue VGA "keyboard application" master menu + detail views
// ================================================================
function AppScreen({ onExit, onBsod }) {
  const [view, setView] = useState("menu");
  const [sel, setSel] = useState(0);
  const [copied, setCopied] = useState(false);
  const now = useNow(1000);
  const clock = formatMT(now);

  const openView = useCallback((key) => {
    selectBeep();
    setView(key);
  }, []);

  const onExitApp = useCallback(() => {
    beep({ freq: 620, duration: 0.06, gain: 0.28 });
    onExit();
  }, [onExit]);

  useEffect(() => {
    const onKey = (e) => {
      if (view === "menu") {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          keyClick();
          setSel((s) => (s + 1) % MENU.length);
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          keyClick();
          setSel((s) => (s - 1 + MENU.length) % MENU.length);
        } else if (e.key === "Enter") {
          e.preventDefault();
          openView(MENU[sel].key);
        } else if (e.key === "Escape") {
          e.preventDefault();
          onExitApp();
        } else if (/^[1-5]$/.test(e.key)) {
          e.preventDefault();
          openView(MENU[parseInt(e.key, 10) - 1].key);
        }
      } else {
        if (e.key === "Escape") {
          e.preventDefault();
          selectBeep();
          setView("menu");
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view, sel, openView, onExitApp]);

  const copyMail = async () => {
    const text = "hello@heisenbergdx.com";
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch (_) { /* fall through to legacy path */ }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        ok = true;
      } catch (_) { /* clipboard not available */ }
    }
    setCopied(true);
    selectBeep();
    setTimeout(() => setCopied(false), 1400);
  };

  const help =
    view === "menu"
      ? "UP/DOWN: SELECT   1-5: JUMP   ENTER: OPEN   ESC: EXIT"
      : "ESC: RETURN TO MASTER MENU";

  return (
    <section className="stage app active" data-testid="app-screen">
      <div className="app-shell">
        <header className="app-header">
          <span className="hdr-left">HEISENBERG DYNAMICS</span>
          <img
            className="white-logo"
            src="/assets/heisenberg-wordmark.png"
            alt="Heisenberg — Advanced Artificial Intelligence Dynamics wordmark"
          />
          <span className="hdr-right">MASTER MENU<br />VERSION 4.86</span>
        </header>

        <main className="app-body">
          <AnimatePresence mode="wait">
            {view === "menu" && (
              <motion.section
                key="menu"
                className="app-view active"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                data-testid="app-view-menu"
              >
                <motion.div
                  className="menu-title"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  GENERATIVE AI SYSTEMS &mdash; MASTER MENU
                </motion.div>
                <div className="menu-question">
                  Select the information module you would like to access:
                </div>
                <ol className="main-menu">
                  {MENU.map((m, i) => (
                    <motion.li
                      key={m.key}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.06 + i * 0.06, duration: 0.28 }}
                    >
                      <button
                        type="button"
                        data-testid={`menu-option-${m.key}`}
                        className={
                          "menu-option" + (i === sel ? " selected" : "")
                        }
                        onMouseEnter={() => setSel(i)}
                        onClick={() => openView(m.key)}
                      >
                        <span>{m.num}</span>
                        <span>{m.label}</span>
                      </button>
                    </motion.li>
                  ))}
                </ol>
                <div className="menu-status" data-testid="menu-status">
                  CURRENT SELECTION: {VIEW_NAMES[MENU[sel].key]}
                </div>
              </motion.section>
            )}

            {view === "overview" && (
              <DetailView
                keyId="overview"
                heading="01 / COMPANY OVERVIEW"
                record="RECORD: HDX-001"
                lead="SPECIALIST CONSULTANCY FOR GENERATIVE AI HARNESS, ARCHITECTURE AND INFRASTRUCTURE."
                rows={[
                  ["MISSION", "TURN GENERATIVE AI INTO RELIABLE, COMPANY-OWNED OPERATIONAL SYSTEMS."],
                  ["METHOD", "DESIGN, BUILD, HARDEN AND INSTALL DIRECTLY IN THE CUSTOMER ENVIRONMENT."],
                  ["TEAM", "TOP SPECIALISTS CURRENT WITH MODELS, TOOLS, CONNECTIONS AND INFRASTRUCTURE."],
                  ["RESULT", "USEFUL AI CAPABILITY THAT COMPOUNDS INSIDE YOUR ORGANIZATION."],
                ]}
              />
            )}

            {view === "services" && (
              <DetailView
                keyId="services"
                heading="02 / CONSULTING SERVICES"
                record="4 MODULES ONLINE"
                rows={[
                  ["AI ARCHITECTURE", "MODELS, RETRIEVAL, MEMORY, ORCHESTRATION, OBSERVABILITY AND PRODUCTION INFRASTRUCTURE."],
                  ["SKILLS + WORKFLOWS", "CUSTOM SKILLS AND CONNECTED WORKFLOWS BUILT AROUND HOW YOUR TEAM ACTUALLY OPERATES."],
                  ["MODEL SPECIALIZATION", "CONTINUED PRE-TRAINING, FINE-TUNING AND EVALUATION FOR NOVEL LLM ADAPTATIONS."],
                  ["RELIABILITY", "GUARDRAILS, EVALUATIONS, THREAT MODELING, FAILOVER, POLICY AND HUMAN CHECKPOINTS."],
                ]}
              />
            )}

            {view === "projects" && (
              <motion.section
                key="projects"
                className="app-view active"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22 }}
                data-testid="app-view-projects"
              >
                <div className="detail-heading">
                  <span>03 / PREVIOUS PROJECTS</span>
                  <span>ARCHIVE ACCESS: GRANTED</span>
                </div>
                {[
                  ["PROJECT 01 - GENERATIVE E-COMMERCE RECOMMENDATION SYSTEM", "BEHAVIORAL SIGNALS + GENERATIVE REASONING FOR MORE RELEVANT, EXPLAINABLE PRODUCT DISCOVERY."],
                  ["PROJECT 02 - AI EMOTIONAL COMPANION", "CONTINUOUS COMPANION THROUGH TELEGRAM AND WHATSAPP WITH DURABLE CONTEXT, MEMORY AND SAFETY-AWARE DIALOGUE."],
                  ["PROJECT 03 - COMPANY-OWNED SPECIALIST LLM", "CONTINUED PRE-TRAINING AND FINE-TUNING PIPELINES FOR PROPRIETARY DOMAINS AND SPECIALIST COMPANY KNOWLEDGE."],
                  ["PROJECT 04 - ADDITIONAL / CLASSIFIED BUILDS", "PRIVATE AGENT PLATFORMS, KNOWLEDGE SYSTEMS, WORKFLOW AUTOMATION AND BESPOKE OPERATIONAL INTERFACES."],
                ].map(([title, copy], i) => (
                  <motion.article
                    key={title}
                    className="project"
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.08, duration: 0.26 }}
                  >
                    <div className="project-title">{title}</div>
                    <div className="project-copy">{copy}</div>
                  </motion.article>
                ))}
              </motion.section>
            )}

            {view === "standards" && (
              <motion.section
                key="standards"
                className="app-view active"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22 }}
                data-testid="app-view-standards"
              >
                <div className="detail-heading">
                  <span>04 / OPERATING STANDARDS</span>
                  <span>OWNERSHIP: CUSTOMER</span>
                </div>
                <p className="lead">
                  EVERY SYSTEM IS BUILT TO TRANSFER. THE CUSTOMER RETAINS THE
                  CAPABILITY, ENVIRONMENT AND INTELLECTUAL PROPERTY.
                </p>
                <div className="system-chain">
                  BUSINESS WORKFLOW &gt;&gt; CUSTOM SKILLS &gt;&gt; MODELS + KNOWLEDGE &gt;&gt; EVALS + GUARDRAILS &gt;&gt; YOUR INFRASTRUCTURE
                </div>
                <div className="check-grid">
                  {[
                    "CUSTOM SKILL CREATION",
                    "CUSTOM WORKFLOWS",
                    "HIGH RELIABILITY",
                    "ACTIVE GUARDRAILS",
                    "CURRENT TOOLCHAIN",
                    "COMPLETE IP TRANSFER",
                    "CUSTOMER INSTALLATION",
                    "VENDOR-AGNOSTIC DESIGN",
                  ].map((s, i) => (
                    <motion.span
                      key={s}
                      className="check"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.06 + i * 0.05, duration: 0.2 }}
                    >
                      {s}
                    </motion.span>
                  ))}
                </div>
              </motion.section>
            )}

            {view === "company" && (
              <motion.section
                key="company"
                className="app-view active"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22 }}
                data-testid="app-view-company"
              >
                <div className="detail-heading">
                  <span>05 / COMPANY DETAILS</span>
                  <span>STATUS: AVAILABLE</span>
                </div>
                <div className="company-grid">
                  <div className="info-box">
                    <h3>REGISTERED OFFICE</h3>
                    HEISENBERG DYNAMICS<br /><br />
                    5830 E 2ND ST<br />
                    STE 7000 #23006<br />
                    CASPER, WYOMING 82609<br />
                    UNITED STATES
                  </div>
                  <div className="info-box">
                    <h3>COMMUNICATIONS</h3>
                    EMAIL<br />
                    <span className="mail" data-testid="company-email">HELLO@HEISENBERGDX.COM</span>
                    <button
                      type="button"
                      className="copy-btn"
                      data-testid="copy-email-btn"
                      onClick={copyMail}
                    >
                      {copied ? "[COPIED]" : "[COPY]"}
                    </button>
                    <br /><br />
                    DOMAIN<br />
                    HEISENBERGDX.COM<br /><br />
                    NEW PROJECTS<br />
                    <span className="bios-ok">ACCEPTING INQUIRIES</span>
                  </div>
                </div>
              </motion.section>
            )}
          </AnimatePresence>
        </main>

        <footer className="app-footer">
          <span data-testid="app-help">{help}</span>
          <span className="marquee" data-testid="app-marquee" aria-hidden="true">
            <span className="marquee-inner">
              HEISENBERG DYNAMICS &nbsp;&middot;&nbsp; GENERATIVE AI HARNESS &nbsp;&middot;&nbsp; ARCHITECTURE &nbsp;&middot;&nbsp; INFRASTRUCTURE &nbsp;&middot;&nbsp; SKILLS + WORKFLOWS &nbsp;&middot;&nbsp; MODEL SPECIALIZATION &nbsp;&middot;&nbsp; RELIABILITY &nbsp;&middot;&nbsp; TRANSFER OF OWNERSHIP &nbsp;&middot;&nbsp; CASPER WY &nbsp;&middot;&nbsp; EST. 2026 &nbsp;&middot;&nbsp;
            </span>
            <span className="marquee-inner" aria-hidden="true">
              HEISENBERG DYNAMICS &nbsp;&middot;&nbsp; GENERATIVE AI HARNESS &nbsp;&middot;&nbsp; ARCHITECTURE &nbsp;&middot;&nbsp; INFRASTRUCTURE &nbsp;&middot;&nbsp; SKILLS + WORKFLOWS &nbsp;&middot;&nbsp; MODEL SPECIALIZATION &nbsp;&middot;&nbsp; RELIABILITY &nbsp;&middot;&nbsp; TRANSFER OF OWNERSHIP &nbsp;&middot;&nbsp; CASPER WY &nbsp;&middot;&nbsp; EST. 2026 &nbsp;&middot;&nbsp;
            </span>
          </span>
          <span data-testid="app-clock">{clock}</span>
        </footer>
      </div>
    </section>
  );
}

function DetailView({ keyId, heading, record, lead, rows }) {
  return (
    <motion.section
      key={keyId}
      className="app-view active"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.22 }}
      data-testid={`app-view-${keyId}`}
    >
      <div className="detail-heading">
        <span>{heading}</span>
        <span>{record}</span>
      </div>
      {lead && <p className="lead">{lead}</p>}
      <div className="rows">
        {rows.map(([label, val], i) => (
          <motion.div
            key={label}
            className="row"
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.06 + i * 0.06, duration: 0.24 }}
          >
            <span className="row-label">{label}</span>
            <span>{val}</span>
          </motion.div>
        ))}
      </div>
    </motion.section>
  );
}

// ================================================================
// BSOD easter egg
// ================================================================
function BSOD({ onDismiss }) {
  useEffect(() => {
    errorTone();
  }, []);
  return (
    <motion.section
      className="stage bsod active"
      data-testid="bsod-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.05 }}
    >
      <div className="bsod-header">Heisenberg</div>
      <p className="bsod-p">
        A fatal exception <b>0E</b> has occurred at <b>0028:C0011E36</b> in
        VXD VMM(01) + 00010E36. The current application will be terminated.
      </p>
      <p className="bsod-p">
        &nbsp; * &nbsp;Press any key to terminate the current application.<br />
        &nbsp; * &nbsp;Press CTRL+ALT+DEL again to restart your computer.
      </p>
      <p className="bsod-p">You will lose any unsaved information in all applications.</p>
      <div className="bsod-press">
        Press any key to continue{" "}
        <span className="bsod-caret" aria-hidden="true">_</span>
      </div>
      <button
        type="button"
        className="bsod-btn"
        data-testid="bsod-dismiss-btn"
        onClick={onDismiss}
      >
        [ANY KEY]
      </button>
    </motion.section>
  );
}

// ================================================================
// ROOT
// ================================================================
export default function App() {
  const [phase, setPhase] = useState("crton"); // crton -> bios -> dosboot -> dos -> app -> bsod -> crtoff -> off
  const [muted, setMutedState] = useState(false);
  const crtRef = useRef(null);

  // Auto power-on on mount (no user click needed)
  useEffect(() => {
    const t = setTimeout(() => setPhase("bios"), 380);
    return () => clearTimeout(t);
  }, []);

  // Prime WebAudio on first user interaction (autoplay policy compliance)
  useEffect(() => {
    const prime = () => {
      primeAudio();
      window.removeEventListener("pointerdown", prime);
      window.removeEventListener("keydown", prime);
    };
    window.addEventListener("pointerdown", prime, { once: true });
    window.addEventListener("keydown", prime, { once: true });
    return () => {
      window.removeEventListener("pointerdown", prime);
      window.removeEventListener("keydown", prime);
    };
  }, []);

  const powerOff = useCallback(() => {
    beep({ freq: 300, duration: 0.15, gain: 0.35, type: "sine" });
    setPhase("crtoff");
    setTimeout(() => setPhase("off"), 700);
  }, []);

  const powerOn = useCallback(() => {
    setPhase("crton");
    setTimeout(() => setPhase("bios"), 750);
  }, []);

  const dismissBsod = useCallback(() => {
    setPhase("dos");
  }, []);

  // Global keys: mute toggle, dismiss BSOD
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "m" || e.key === "M") {
        if (phase !== "off") {
          const next = !muted;
          setMuted(next);
          setMutedState(next);
          beep({ freq: 660, duration: 0.05, gain: 0.25 });
        }
      }
      if (phase === "bsod") {
        dismissBsod();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, muted, dismissBsod]);

  useEffect(() => {
    const onBsod = () => setPhase("bsod");
    window.addEventListener("hdx-bsod", onBsod);
    return () => window.removeEventListener("hdx-bsod", onBsod);
  }, []);

  // Mouse-driven subtle parallax on scanlines (very small — respects DOS feel)
  const [px, setPx] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const onMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setPx({ x, y });
    };
    window.addEventListener("pointermove", onMove);
    return () => window.removeEventListener("pointermove", onMove);
  }, []);

  const crtClass =
    "monitor" +
    (phase === "off" ? " off" : "") +
    (phase === "crton" ? " crt-on" : "") +
    (phase === "crtoff" ? " crt-off" : "");

  return (
    <div className={crtClass} ref={crtRef}>
      <div
        className="crt-frame"
        style={{
          "--px": px.x,
          "--py": px.y,
        }}
      >
        <div className="screen">
          <div className="noise" aria-hidden="true" />
          <div className="scanlines" aria-hidden="true" />
          <div className="chroma" aria-hidden="true" />
          <div className="vignette" aria-hidden="true" />

          <AnimatePresence mode="wait">
            {phase === "off" && <PowerOnScreen key="power" onPowerOn={powerOn} />}
            {phase === "crton" && (
              <motion.div
                key="crton"
                className="crt-flash"
                initial={{ scaleY: 0.02, opacity: 1 }}
                animate={{ scaleY: 1, opacity: [1, 1, 0.8] }}
                transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
              />
            )}
            {phase === "crtoff" && (
              <motion.div
                key="crtoff"
                className="crt-flash"
                initial={{ scaleY: 1, opacity: 1 }}
                animate={{ scaleY: 0.005, opacity: [1, 1, 0] }}
                transition={{ duration: 0.55 }}
              />
            )}
            {phase === "bios" && (
              <BiosScreen key="bios" onSkip={() => setPhase("dosboot")} />
            )}
            {phase === "dosboot" && (
              <DosBootScreen key="dosboot" onComplete={() => setPhase("dos")} />
            )}
            {phase === "dos" && (
              <DosPrompt
                key="dos"
                onLaunch={() => setPhase("app")}
                onPowerOff={powerOff}
              />
            )}
            {phase === "app" && (
              <AppScreen
                key="app"
                onExit={() => setPhase("dos")}
                onBsod={() => setPhase("bsod")}
              />
            )}
            {phase === "bsod" && <BSOD key="bsod" onDismiss={dismissBsod} />}
          </AnimatePresence>

          {/* HUD overlay: mute + status */}
          {phase !== "off" && phase !== "crtoff" && (
            <div className="hud" aria-hidden="false">
              <button
                type="button"
                className="hud-mute"
                data-testid="hud-mute-btn"
                onClick={() => {
                  const next = !muted;
                  setMuted(next);
                  setMutedState(next);
                }}
              >
                {muted ? "[ AUDIO: OFF ]" : "[ AUDIO: ON ]"}
              </button>
              <div className="hud-brand">HDX &middot; W6168MS</div>
            </div>
          )}

          {/* Portrait-mobile: prompt to rotate the phone for the intended experience */}
          <div className="rotate-hint" data-testid="rotate-hint" aria-hidden="true">
            <div className="rotate-inner">
              <div className="rotate-icon">
                <svg viewBox="0 0 100 100" width="72" height="72" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="square" strokeLinejoin="miter">
                  <rect x="28" y="8" width="44" height="70" rx="4" />
                  <line x1="42" y1="72" x2="58" y2="72" />
                  <path d="M12 62 Q 12 92 42 92" />
                  <polyline points="12,50 12,62 24,62" />
                </svg>
              </div>
              <div className="rotate-title">ROTATE YOUR DEVICE</div>
              <div className="rotate-copy">
                THIS TERMINAL IS OPTIMIZED FOR LANDSCAPE 4:3 CRT DISPLAY.<br />
                TURN YOUR PHONE SIDEWAYS FOR THE INTENDED EXPERIENCE.
              </div>
              <div className="rotate-blink">&gt; AWAITING SIGNAL_</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
